import React, { useState, useEffect, useRef } from 'react';
import {
	Box,
	Paper,
	TextField,
	Button,
	Typography,
	Alert,
	Container,
	Card,
	CardContent,
	IconButton,
	Tooltip,
	Chip
} from '@mui/material';
import { ContentCopy, CheckCircle } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles((theme) => ({
	root: {
		minHeight: '100vh',
		padding: theme.spacing(4),
		backgroundColor: '#f5f5f5'
	},
	container: {
		maxWidth: '800px',
		margin: '0 auto'
	},
	paper: {
		padding: theme.spacing(4),
		marginBottom: theme.spacing(3)
	},
	title: {
		marginBottom: theme.spacing(3),
		color: '#004F2B',
		fontWeight: 600
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(3)
	},
	embedCodeCard: {
		marginTop: theme.spacing(3),
		backgroundColor: '#f8f9fa'
	},
	codeBlock: {
		backgroundColor: '#2d2d2d',
		color: '#f8f8f2',
		padding: theme.spacing(2),
		borderRadius: theme.spacing(1),
		fontFamily: 'monospace',
		fontSize: '0.875rem',
		overflowX: 'auto',
		position: 'relative',
		marginTop: theme.spacing(2)
	},
	copyButton: {
		position: 'absolute',
		top: theme.spacing(1),
		right: theme.spacing(1)
	},
	instructions: {
		marginTop: theme.spacing(2),
		padding: theme.spacing(2),
		backgroundColor: '#e3f2fd',
		borderRadius: theme.spacing(1)
	},
	successIcon: {
		color: '#4caf50'
	}
}));

export default function WidgetConfig() {
	const classes = useStyles();
	const [userId, setUserId] = useState('');
	const [orgId, setOrgId] = useState('');
	const [copied, setCopied] = useState(false);
	const [error, setError] = useState('');
	const [selectedProperty, setSelectedProperty] = useState(null);
	const iframeRef = useRef(null);

	const baseUrl = window.location.origin;
	const widgetUrl = `${baseUrl}/widget`;

	// Listen for messages from the iframe
	useEffect(() => {
		const handleMessage = (event) => {
			// In production, verify event.origin for security
			if (event.data && event.data.type === 'WIDGET_HOME_SELECTED') {
				setSelectedProperty(event.data.data);
				console.log('Property selected in preview:', event.data.data);
			}
		};

		window.addEventListener('message', handleMessage);
		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, []);

	const generateEmbedCode = () => {
		if (!userId || !orgId) {
			setError('Please enter both User ID and Organization ID');
			return;
		}
		setError('');
		setCopied(false);
	};

	const embedCode =
		userId && orgId
			? `<iframe 
  src="${widgetUrl}?userId=${encodeURIComponent(userId)}&orgId=${encodeURIComponent(orgId)}"
  width="100%"
  height="400"
  frameborder="0"
  style="border: none; border-radius: 8px;"
  title="Property Search Widget"
></iframe>`
			: '';

	const handleCopy = () => {
		if (embedCode) {
			navigator.clipboard.writeText(embedCode).then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			});
		}
	};

	const handleGenerate = () => {
		generateEmbedCode();
	};

	return (
		<Box className={classes.root}>
			<Container className={classes.container}>
				<Paper className={classes.paper} elevation={3}>
					<Typography variant="h4" className={classes.title}>
						Widget Integration Setup
					</Typography>
					<Typography variant="body1" color="textSecondary" paragraph>
						Enter your User ID and Organization ID to generate an embed code for the
						Property Search Widget.
					</Typography>

					<Box className={classes.form}>
						<TextField
							label="User ID"
							value={userId}
							onChange={(e) => {
								setUserId(e.target.value);
								setError('');
								setCopied(false);
							}}
							fullWidth
							required
							variant="outlined"
							placeholder="Enter your User ID"
						/>

						<TextField
							label="Organization ID"
							value={orgId}
							onChange={(e) => {
								setOrgId(e.target.value);
								setError('');
								setCopied(false);
							}}
							fullWidth
							required
							variant="outlined"
							placeholder="Enter your Organization ID"
						/>

						{error && <Alert severity="error">{error}</Alert>}

						<Button
							variant="contained"
							color="primary"
							onClick={handleGenerate}
							size="large"
							disabled={!userId || !orgId}
							sx={{ mt: 2 }}
						>
							Generate Embed Code
						</Button>
					</Box>

					{embedCode && (
						<Card className={classes.embedCodeCard}>
							<CardContent>
								<Box
									display="flex"
									justifyContent="space-between"
									alignItems="center"
									mb={2}
								>
									<Typography variant="h6">Embed Code</Typography>
									<Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
										<IconButton onClick={handleCopy} size="small">
											{copied ? (
												<CheckCircle className={classes.successIcon} />
											) : (
												<ContentCopy />
											)}
										</IconButton>
									</Tooltip>
								</Box>
								<Box className={classes.codeBlock}>
									<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
										{embedCode}
									</pre>
								</Box>

								<Box className={classes.instructions}>
									<Typography variant="subtitle2" gutterBottom>
										<strong>Instructions:</strong>
									</Typography>
									<ol style={{ margin: 0, paddingLeft: 20 }}>
										<li>Copy the embed code above</li>
										<li>
											Paste it into your website's HTML where you want the
											widget to appear
										</li>
										<li>
											The widget will automatically load with your User ID and
											Organization ID
										</li>
										<li>
											When a user selects a property, the parent window will
											receive a message with the property data
										</li>
									</ol>
								</Box>

								<Box
									className={classes.instructions}
									sx={{ mt: 2, backgroundColor: '#fff3cd' }}
								>
									<Typography variant="subtitle2" gutterBottom>
										<strong>Listening for Widget Events:</strong>
									</Typography>
									<Typography
										variant="body2"
										component="pre"
										sx={{
											fontFamily: 'monospace',
											fontSize: '0.75rem',
											whiteSpace: 'pre-wrap'
										}}
									>
										{`window.addEventListener('message', (event) => {
  if (event.data.type === 'WIDGET_HOME_SELECTED') {
    const { address, city, homeId } = event.data.data;
    // Handle the selected property
    console.log('Selected property:', { address, city, homeId });
  }
});`}
									</Typography>
								</Box>
							</CardContent>
						</Card>
					)}
				</Paper>

				<Paper className={classes.paper} elevation={2}>
					<Typography variant="h6" gutterBottom>
						Preview
					</Typography>
					{embedCode ? (
						<>
							{selectedProperty && (
								<Alert
									severity="success"
									sx={{ mb: 2 }}
									onClose={() => setSelectedProperty(null)}
								>
									<Box>
										<Typography variant="subtitle2" gutterBottom>
											Property Selected:
										</Typography>
										<Box display="flex" flexWrap="wrap" gap={1}>
											{selectedProperty.address && (
												<Chip
													label={`Address: ${selectedProperty.address}`}
													size="small"
												/>
											)}
											{selectedProperty.city && (
												<Chip
													label={`City: ${selectedProperty.city}`}
													size="small"
												/>
											)}
											{selectedProperty.homeId && (
												<Chip
													label={`Home ID: ${selectedProperty.homeId}`}
													size="small"
												/>
											)}
										</Box>
									</Box>
								</Alert>
							)}
							<Box
								sx={{
									border: '2px dashed #ccc',
									borderRadius: 2,
									padding: 2,
									minHeight: '400px'
								}}
							>
								<iframe
									ref={iframeRef}
									src={`${widgetUrl}?userId=${encodeURIComponent(
										userId
									)}&orgId=${encodeURIComponent(orgId)}`}
									width="100%"
									height="400"
									frameBorder="0"
									style={{ border: 'none', borderRadius: '8px' }}
									title="Property Search Widget Preview"
								/>
							</Box>
							<Typography
								variant="caption"
								color="textSecondary"
								sx={{ mt: 1, display: 'block' }}
							>
								💡 Try selecting an address from the dropdown above to see the
								message event in action!
							</Typography>
						</>
					) : (
						<Typography variant="body2" color="textSecondary">
							Enter your User ID and Organization ID above to see a preview
						</Typography>
					)}
				</Paper>
			</Container>
		</Box>
	);
}
